/**
 * Séquence de pose d'un bâtiment — E§1.1, intégralement.
 *
 * Ce fichier n'existe pas pour « faire joli » : la pose est **le geste central** du jeu,
 * répété des milliers de fois. Son ressenti décide de l'envie de recommencer.
 *
 * L'enchaînement, dans l'ordre :
 *
 *   1. la tuile cible s'enfonce et s'éclaircit — 80 ms d'anticipation ;
 *   2. le bâtiment tombe de 40 px **avec accélération**, jamais en fondu ;
 *   3. il s'écrase à l'impact (1,15 × 0,85) puis rebondit en `easeOutBack` ;
 *   4. un anneau de poussière se dilate depuis la base ;
 *   5. les voisins sursautent de 3 px, décalés de 60 ms ;
 *   6. impact grave + carillon aigu qui monte d'un demi-ton par pose consécutive.
 *
 * Toutes les valeurs viennent de `config/juice.ts` : le ressenti se règle sans toucher
 * ici. Le mouvement réduit est géré par le `TweenRunner`, qui applique instantanément
 * sans jamais supprimer le retour.
 */

import { JUICE } from '@/config/juice';
import type { TweenRunner } from '@/core/tween';
import type { Container, Sprite } from 'pixi.js';
import { Graphics } from 'pixi.js';
import { isGone } from './spriteGuard';

export interface PlacementTargets {
  /** Sprite du bâtiment qui vient d'être posé. */
  readonly building: Sprite;
  /** Sprite de la tuile sous le bâtiment. */
  readonly tile: Sprite | undefined;
  /** Sprites des bâtiments adjacents, du plus proche au plus lointain. */
  readonly neighbours: readonly Sprite[];
  /** Calque où insérer les effets éphémères. */
  readonly effectsLayer: Container;
  /** Position monde de la case, pour l'onde de choc. */
  readonly worldX: number;
  readonly worldY: number;
}

export interface PlacementJuiceOptions {
  readonly runner: TweenRunner;
  readonly now: number;
  /** Déclenché à l'instant de l'impact, pour synchroniser le son au contact. */
  readonly onImpact?: () => void;
}

/**
 * Joue la séquence complète. Rend une fonction d'annulation — un changement d'écran
 * en pleine animation ne doit pas laisser un bâtiment à mi-chute.
 */
export function playPlacementJuice(
  targets: PlacementTargets,
  options: PlacementJuiceOptions,
): () => void {
  const { runner, now } = options;
  const { placement } = JUICE;
  const handles: { cancel: () => void }[] = [];
  /** Graphiques éphémères créés ici : à détruire aussi en cas d'annulation. */
  const owned: Graphics[] = [];

  const restY = targets.building.y;
  const restScaleX = targets.building.scale.x;
  const restScaleY = targets.building.scale.y;

  /*
    Le moteur détruit et recrée les sprites en cours de route — amélioration,
    rotation de vue, resynchronisation — et c'est ASSUMÉ : « un sprite destroyed
    vaut absent, l'état de jeu fait foi ». Une séquence de juice qui garde une
    référence doit donc traiter la destruction comme une annulation, pas comme un
    cas impossible. Sans cette garde, `scale.set` sur un sprite détruit levait une
    TypeError DANS le ticker PixiJS, et tuait la boucle de rendu entière.

    La garde vit dans `spriteGuard.ts` : le même défaut est apparu sur le
    déroulement des routes (audit P0.1), et une garde recopiée aurait divergé.
  */
  const gone = isGone;

  /* 1. Anticipation — la tuile s'enfonce et s'éclaircit avant que rien n'arrive.
        C'est ce quart de seconde qui fait qu'on « sent venir » l'impact. */
  const tile = targets.tile;
  if (tile !== undefined) {
    const tileRestY = tile.y;

    /*
      L'éclaircissement passe par un losange blanc superposé, pas par `tint` : la
      teinte de PixiJS **multiplie** la couleur du sprite, elle ne sait donc
      qu'assombrir. Vouloir éclaircir avec elle ne produirait rien du tout.
    */
    const highlight = new Graphics();
    highlight.x = targets.worldX;
    highlight.y = targets.worldY;
    highlight.alpha = 0;
    owned.push(highlight);
    targets.effectsLayer.addChild(highlight);
    highlight
      .moveTo(0, -145)
      .lineTo(256, 0)
      .lineTo(0, 145)
      .lineTo(-256, 0)
      .closePath()
      .fill({ color: 0xffffff });

    handles.push(
      runner.add(
        {
          from: 0,
          to: 1,
          durationMs: placement.anticipationMs,
          easing: 'out',
          onUpdate: (t) => {
            if (!gone(tile)) tile.y = tileRestY + placement.anticipationDepthPx * t;
            if (!gone(highlight)) highlight.alpha = t * 0.3;
          },
        },
        now,
      ),
      runner.add(
        {
          from: 1,
          to: 0,
          durationMs: placement.reboundMs,
          delayMs: placement.anticipationMs + placement.dropMs,
          easing: 'out',
          onUpdate: (t) => {
            if (!gone(tile)) tile.y = tileRestY + placement.anticipationDepthPx * t;
            if (!gone(highlight)) highlight.alpha = t * 0.3;
          },
          onComplete: () => {
            if (!gone(tile)) tile.y = tileRestY;
            if (!gone(highlight)) highlight.destroy();
          },
        },
        now,
      ),
    );
  }

  /* 2. Chute accélérée. Pas de fondu : un bâtiment qui apparaît en transparence
        n'a aucun poids, il ne s'est rien passé. */
  targets.building.y = restY - placement.dropHeightPx;
  targets.building.alpha = 1;

  handles.push(
    runner.add(
      {
        from: restY - placement.dropHeightPx,
        to: restY,
        durationMs: placement.dropMs,
        delayMs: placement.anticipationMs,
        easing: 'in',
        onUpdate: (y) => {
          if (!gone(targets.building)) targets.building.y = y;
        },
        onComplete: () => {
          // Bâtiment détruit en pleine chute : plus rien à écraser ni à faire
          // sonner — un impact sonore sans rien à l'écran serait un mensonge.
          if (gone(targets.building)) return;
          options.onImpact?.();
          squashAndRebound();
          shockwave();
          nudgeNeighbours();
        },
      },
      now,
    ),
  );

  /* 3. Écrasement puis rebond élastique. L'écrasement est instantané, le retour
        prend son temps — l'inverse donnerait un mouvement mou. */
  function squashAndRebound(): void {
    if (gone(targets.building)) return;
    targets.building.scale.set(
      restScaleX * placement.squashScaleX,
      restScaleY * placement.squashScaleY,
    );

    handles.push(
      runner.add(
        {
          from: 0,
          to: 1,
          durationMs: placement.reboundMs,
          easing: 'back',
          onUpdate: (t) => {
            if (gone(targets.building)) return;
            targets.building.scale.set(
              restScaleX * (placement.squashScaleX + (1 - placement.squashScaleX) * t),
              restScaleY * (placement.squashScaleY + (1 - placement.squashScaleY) * t),
            );
          },
          onComplete: () => {
            if (gone(targets.building)) return;
            targets.building.scale.set(restScaleX, restScaleY);
          },
        },
        performance.now(),
      ),
    );
  }

  /* 4. Onde de poussière : un anneau qui se dilate et s'efface depuis la base. */
  function shockwave(): void {
    const ring = new Graphics();
    ring.x = targets.worldX;
    ring.y = targets.worldY;
    owned.push(ring);
    targets.effectsLayer.addChild(ring);

    handles.push(
      runner.add(
        {
          from: 0,
          to: 1,
          durationMs: placement.shockwaveMs,
          easing: 'out',
          onUpdate: (t) => {
            if (gone(ring)) return;
            const radius = 40 + t * 180;
            ring.clear();
            ring
              .ellipse(0, 0, radius, radius * 0.566)
              .stroke({ width: 8 * (1 - t) + 1, color: 0xffe6b8, alpha: 0.55 * (1 - t) });
          },
          onComplete: () => {
            if (!gone(ring)) ring.destroy();
          },
        },
        performance.now(),
      ),
    );
  }

  /* 5. Sursaut du voisinage : la ville « ressent » la construction. Le décalage
        est ce qui transforme une secousse collective en onde qui se propage. */
  function nudgeNeighbours(): void {
    targets.neighbours.forEach((neighbour, index) => {
      const rest = neighbour.y;
      handles.push(
        runner.add(
          {
            from: 0,
            to: 1,
            durationMs: placement.reboundMs,
            delayMs: index * placement.neighbourDelayMs,
            easing: 'out',
            onUpdate: (t) => {
              if (gone(neighbour)) return;
              // Une seule oscillation amortie : un sursaut, pas un tremblement.
              neighbour.y = rest - Math.sin(t * Math.PI) * placement.neighbourJumpPx * (1 - t);
            },
            onComplete: () => {
              if (!gone(neighbour)) neighbour.y = rest;
            },
          },
          performance.now(),
        ),
      );
    });
  }

  return () => {
    for (const handle of handles) handle.cancel();
    // Les graphiques éphémères sont à NOUS : sans ce nettoyage, une annulation en
    // cours de séquence laissait l'anneau et le halo orphelins dans le calque.
    for (const graphic of owned) {
      if (!gone(graphic)) graphic.destroy();
    }
    if (!gone(targets.building)) {
      targets.building.y = restY;
      targets.building.scale.set(restScaleX, restScaleY);
    }
  };
}
